import React, { useRef, useState } from 'react';
import { message } from 'antd';
import {
  validateDomainUrl,
  getRSAKeyPairForCloud,
  getOauthTokensForCloud,
} from '../jiraOAuthCloud.action';
import trooprMark from '../../../media/circular_troopr_logo.svg';
import './jiraConnect.css';

// Polished 4-step Jira (Cloud) connection wizard. Design source of truth:
// design_handoff_troopr_onboarding/Jira Connection.dc.html. Framed in the same
// centered "app window" as the onboarding wizard for visual continuity. Wired to
// the real OAuth 1.0a Cloud app-link flow:
//   step 0 URL       -> validateDomainUrl
//   step 1 Create Link (paste app URL)
//   step 2 Configure -> getRSAKeyPairForCloud (returns consumerkey/publicKey/sessionid)
//   step 3 Secure    -> getOauthTokensForCloud(sessionid) -> Jira consent -> callback saves connection

const APP_LINK_URL = 'https://app.troopr.io';
const APP_NAME = 'Troopr';
const URL_RE = /^https?:\/\/[a-z0-9-]+\.atlassian\.net\/?$/i;

const PHASES = [
  { tag: 'STEP 01 · WORKSPACE', label: 'Jira URL' },
  { tag: 'STEP 02 · CREATE', label: 'Create Link' },
  { tag: 'STEP 03 · CONFIGURE', label: 'Configure Link' },
  { tag: 'STEP 04 · SECURE', label: 'Secure Link' },
];

const JiraConnectWizard = ({ wId, subSkill, from, onCancel }) => {
  const [step, setStep] = useState(0);
  const [reached, setReached] = useState(0);
  const [jiraUrl, setJiraUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [creds, setCreds] = useState({ consumerkey: '', publicKey: '', sessionid: '' });
  const [copied, setCopied] = useState(null);
  const copyTimer = useRef(null);

  const urlOk = URL_RE.test((jiraUrl || '').trim());
  const canNext = step !== 0 || urlOk;

  const goTo = (i) => { if (i <= reached) setStep(i); };
  const advance = () => setStep((s) => { const ns = Math.min(s + 1, 3); setReached((r) => Math.max(r, ns)); return ns; });
  const back = () => setStep((s) => Math.max(0, s - 1));

  const copy = (key, value) => {
    try {
      const p = navigator.clipboard && navigator.clipboard.writeText(value);
      if (p && p.catch) p.catch(() => {});
    } catch (_) {}
    setCopied(key);
    clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(null), 1600);
  };

  const next = async () => {
    if (!canNext || loading) return;

    if (step === 0) {
      setLoading(true);
      const valid = await validateDomainUrl(wId, jiraUrl.trim());
      if (!valid || !valid.instanceInfo) {
        setLoading(false);
        return message.error('Could not verify that Jira URL. Check it and try again.');
      }
      const info = await getRSAKeyPairForCloud(wId, valid.instanceInfo.baseUrl || jiraUrl.trim(), subSkill);
      setLoading(false);
      if (!info || !info.sessionid) {
        return message.error('Could not start the Jira connection. Please try again.');
      }
      setCreds({
        consumerkey: info.consumerkey || 'troopr',
        publicKey: info.publicKey || '',
        sessionid: info.sessionid,
      });
      return advance();
    }

    if (step < 3) return advance();

    setLoading(true);
    const data = await getOauthTokensForCloud(wId, creds.sessionid, from);
    setLoading(false);
    if (data && data.url) {
      window.location.assign(data.url); // Jira consent → callback saves the connection
    } else {
      message.error(
        (data && data.error) ||
          'Could not connect. Make sure the application link was created exactly as shown, then try again.'
      );
      setStep(0);
    }
  };

  const phaseContent = () => {
    if (step === 1) {
      return {
        sub: 'Create a new application link inside your Jira admin settings.',
        adminNotice: true,
        substeps: [
          { num: '1', title: 'Open your Jira application links page.', link: { label: 'Open application links', href: `${jiraUrl.replace(/\/$/, '')}/plugins/servlet/applinks/listApplicationLinks` } },
          { num: '2', title: 'Click "Create new link", then paste this URL into the dialog.', fields: [{ key: 'appurl', value: APP_LINK_URL, hint: 'Any unique URL works here.' }] },
          { num: '3', title: 'Click "Continue" on the Configure Application URL form.' },
        ],
      };
    }
    if (step === 2) {
      return {
        sub: 'Fill in the link details — you can ignore every field except these.',
        substeps: [
          { num: '4', title: 'Set the application name to the value below.', fields: [{ key: 'appname', value: APP_NAME }] },
          { num: '5', title: 'Scroll to the bottom and tick the "Create incoming link" checkbox. Ignore all other fields.' },
          { num: '6', title: 'Click "Continue".' },
        ],
      };
    }
    return {
      sub: 'Paste these credentials into the incoming-link form to finish.',
      substeps: [
        {
          num: '7',
          title: 'Copy each value into the matching field on the form.',
          fields: [
            { key: 'ckey', value: creds.consumerkey, label: 'Consumer Key' },
            { key: 'cname', value: 'troopr', label: 'Consumer Name' },
            { key: 'pkey', value: creds.publicKey, label: 'Public Key' },
          ],
        },
        { num: '8', title: 'Click "Continue" to create the application link, then hit Connect below.' },
      ],
    };
  };

  const renderField = (f) => {
    const isCopied = copied === f.key;
    return (
      <div key={f.key} className="jcw__field">
        {f.label && <div className="jcw__field-label">{f.label}</div>}
        <div className="jcw__field-row">
          <div className="jcw__field-value">{f.value || '—'}</div>
          <button className={'jcw__copy' + (isCopied ? ' jcw__copy--done' : '')} onClick={() => copy(f.key, f.value)}>
            {isCopied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
        {f.hint && <div className="jcw__field-hint">{f.hint}</div>}
      </div>
    );
  };

  const content = step > 0 ? phaseContent() : null;

  return (
    <div className="jcw">
      <div className="jcw__window">
        <header className="jcw__header">
          <div className="jcw__brand">
            <img src={trooprMark} alt="" className="jcw__brand-logo" />
            <span className="jcw__brand-name">Troopr</span>
            <span className="jcw__brand-sep">›</span>
            <span className="jcw__brand-crumb">Connect Jira</span>
          </div>
          <div className="jcw__stepper">
            {PHASES.map((p, i) => {
              const status = i < step ? 'done' : i === step ? 'active' : 'todo';
              return (
                <div key={p.label} className="jcw__step-wrap" style={{ flex: i === 0 ? '0 0 auto' : 1 }}>
                  {i > 0 && <div className={'jcw__connector' + (i <= step ? ' jcw__connector--on' : '')} />}
                  <div className="jcw__node" style={{ cursor: i <= reached ? 'pointer' : 'default' }} onClick={() => goTo(i)}>
                    <span className={'jcw__circle jcw__circle--' + status}>{status === 'done' ? '✓' : i + 1}</span>
                    <span className={'jcw__step-label jcw__step-label--' + status}>{p.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </header>

        <main className="jcw__body">
          <div className="jcw__inner">
            {step === 0 ? (
              <div className="jcw__url">
                <div className="jcw__url-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#5a3ec8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
                    <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
                  </svg>
                </div>
                <h1 className="jcw__h1">Let's connect to Jira</h1>
                <p className="jcw__lead">
                  Enter your Jira site URL to get started. You'll set up a secure application link in
                  the next few steps.
                </p>
                <label className="jcw__url-label">Jira site URL</label>
                <div className={'jcw__url-box' + (urlOk ? ' jcw__url-box--ok' : '')}>
                  <span className="jcw__url-globe">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#a8a69e" strokeWidth="1.6">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M3 12h18" strokeLinecap="round" />
                      <path d="M12 3c2.6 2.7 2.6 15.3 0 18M12 3c-2.6 2.7-2.6 15.3 0 18" strokeLinecap="round" />
                    </svg>
                  </span>
                  <input
                    className="jcw__url-input"
                    value={jiraUrl}
                    onChange={(e) => setJiraUrl(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && urlOk && next()}
                    placeholder="https://your-domain.atlassian.net"
                  />
                  {urlOk && <span className="jcw__verified">✓ Verified</span>}
                </div>
                <div className="jcw__url-hint">Example: https://domain-name.atlassian.net</div>
              </div>
            ) : (
              <div className="jcw__phase">
                <div className="jcw__phase-head">
                  <div className="jcw__phase-tag">{PHASES[step].tag}</div>
                  <h1 className="jcw__h1 jcw__h1--phase">
                    {step === 1 ? 'Create the application link' : step === 2 ? 'Configure the application link' : 'Secure the application link'}
                  </h1>
                  <p className="jcw__phase-sub">{content.sub}</p>
                </div>

                {content.adminNotice && (
                  <div className="jcw__admin">
                    <span className="jcw__admin-icon">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round">
                        <path d="M12 7v6M12 17v.01" />
                      </svg>
                    </span>
                    <span className="jcw__admin-text">
                      Only a <strong>Jira admin</strong> can complete this setup.
                    </span>
                  </div>
                )}

                <div className="jcw__timeline">
                  {content.substeps.map((st, i) => (
                    <div key={st.num} className="jcw__substep">
                      <div className="jcw__substep-rail">
                        {i < content.substeps.length - 1 && <div className="jcw__substep-line" />}
                        <span className="jcw__substep-num">{st.num}</span>
                      </div>
                      <div className="jcw__substep-body">
                        <div className="jcw__substep-title">{st.title}</div>
                        {st.link && (
                          <a className="jcw__substep-link" href={st.link.href} target="_blank" rel="noopener noreferrer">
                            {st.link.label} <span>↗</span>
                          </a>
                        )}
                        {st.fields && st.fields.map(renderField)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>

        <footer className="jcw__footer">
          <div className="jcw__footer-hint">
            {step === 0 && !urlOk ? 'ENTER A VALID JIRA URL TO CONTINUE' : `STEP ${String(step + 1).padStart(2, '0')} / 04`}
          </div>
          <div className="jcw__footer-actions">
            {step > 0 ? (
              <button className="jcw__btn-ghost" onClick={back}>Previous</button>
            ) : (
              onCancel && <button className="jcw__btn-ghost" onClick={onCancel}>Cancel</button>
            )}
            <button className={'jcw__btn-primary' + (!canNext ? ' jcw__btn-primary--off' : '')} disabled={!canNext || loading} onClick={next}>
              {loading ? 'Please wait…' : step === 3 ? 'Connect' : 'Next'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default JiraConnectWizard;
